import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import { Step } from '@mui/material';
import MUIMobileStepper from '@mui/material/MobileStepper';
import MUIStepper from '@mui/material/Stepper';
import { BookingSteps } from '@model/common';
import { mq } from '@styles/breakpoints';
import { Theme, ThemeProps, DisableableThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import React from 'react';
import { Icon } from '../generic-icon';
import { ComponentContainer } from '@styles/layout';
import { withIsServer } from '@components/hoc';
import { StepperLabel } from '@components/common/stepper/StepperLabel';

/* ***************** *
 *       Types       *
 * ***************** */
export interface StepperProps {
  theme?: Theme;
  testId?: string;
  steps: Array<BookingSteps>;
  activeStep: number;
  onClick: (step: BookingSteps) => void;
  isServer?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({
  ['.MuiStepper-root']: {
    paddingLeft: 10,
    paddingRight: 10
  }
});

const DesktopStepperContainer = styled.div(({ theme }: ThemeProps) => ({
  display: 'none',
  ['.MuiStep-horizontal']: {
    flex: '1 1 auto',
    minWidth: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  [mq.small]: {
    display: 'block',
    ['.MuiStepLabel-label, .MuiStepIcon-text']: {
      ...(theme.custom.typography.labels.default as any)
    },

    ['.MuiStepper-root']: {
      padding: theme.custom.spacing.medium
    },

    ['.MuiStepLabel-label, .MuiStepIcon-root']: {
      color: theme.custom.colors.group4.light,
      transition: `color ${theme.custom.transitions.default}`
    },

    ['.MuiStepIcon-root']: {
      fontSize: '1.8rem'
    }
  },
  [mq.medium]: {
    ['.MuiStep-horizontal']: {
      flex: 'none'
    }
  }
}));

const MobileStepperContainer = styled.div(({ theme }: ThemeProps) => ({
  ['.MuiMobileStepper-root']: {
    backgroundColor: 'transparent',
    paddingTop: theme.custom.spacing.medium,
    paddingBottom: theme.custom.spacing.medium
  },

  ['.MuiMobileStepper-dot']: {
    backgroundColor: theme.custom.colors.group1.base,
    margin: `0 ${theme.custom.spacing.medium / 2}px`,
    width: 10,
    height: 10
  },

  ['.MuiMobileStepper-dotActive']: {
    ['~ .MuiMobileStepper-dot']: {
      backgroundColor: theme.custom.colors.group4.lighter
    }
  },

  [mq.small]: {
    display: 'none'
  }
}));

const StepperButton: any = styled.a(({ theme, isDisabled }: DisableableThemeProps) => ({
  minWidth: 60,
  ...(theme.custom.typography.labels.default as any),
  color: isDisabled ? theme.custom.colors.group4.lighter : theme.custom.colors.group4.light,
  textDecoration: 'none',
  textTransform: 'uppercase',
  transition: `color ${theme.custom.transitions.default}`,
  cursor: isDisabled ? 'default' : 'pointer',

  ['i']: {
    color: isDisabled ? theme.custom.colors.group4.lighter : theme.custom.colors.group4.light
  },

  ...(!isDisabled && {
    ['&:hover']: {
      color: theme.custom.states.link.hover
    }
  })
}));

interface StepperItemProps extends ThemeProps {
  disabled: boolean;
}
const StepperNavButtonWrapper: any = styled(Step)(({ theme, disabled }: StepperItemProps) => ({
  cursor: !disabled ? 'pointer' : 'default',

  ...(!disabled && {
    ['&:hover']: {
      ['.MuiStepLabel-label']: {
        color: theme.custom.states.link.hover
      }
    }
  })
}));

/* ********************** *
 *    StepperComponent    *
 * ********************** */

export const StepperComponent = (props: StepperProps) => {
  const { steps, activeStep, onClick, isServer } = props;
  const t: any = useI18NextContext();

  if (isServer) {
    return null;
  }

  /* *** RENDERERS *** */
  return (
    <ComponentContainer noPadding>
      <Container data-testid={props.testId || TestId.stepper.main}>
        <DesktopStepperContainer>
          <MUIStepper activeStep={activeStep}>
            {steps.map((label: string, index: number) => (
              <StepperNavButtonWrapper
                data-testid={TestId.stepper.step}
                key={label}
                disabled={index >= activeStep}
                onClick={() => index < activeStep && onClick(steps[index])}
              >
                <StepperLabel
                  label={label}
                  disabled={index > activeStep}
                  active={index === activeStep}
                  completed={index < activeStep}
                  index={index}
                />
              </StepperNavButtonWrapper>
            ))}
          </MUIStepper>
        </DesktopStepperContainer>
        <MobileStepperContainer>
          <MUIMobileStepper
            variant="dots"
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            nextButton={<StepperButton />}
            backButton={
              <StepperButton
                data-testid={TestId.stepper.mobileBackButton}
                isDisabled={activeStep === 0}
                onClick={() => activeStep > 0 && onClick(steps[activeStep - 1])}
              >
                <Icon name={'mhi-chevron-left'} /> {t('common__label--back')}
              </StepperButton>
            }
          />
        </MobileStepperContainer>
      </Container>
    </ComponentContainer>
  );
};

export const Stepper = withTheme(withIsServer(StepperComponent));
